import axiosInstance from '@/app/api/axiosInstance';
import { Party, LastOrderInfo } from '@/app/types/party';

/**
 * Get a single party by ID
 */
export const getPartyById = async (partyId: number): Promise<Party> => {
    const response = await axiosInstance.get<Party>(`/parties/${partyId}`);
    return response.data;
};

/**
 * Get recent sales invoices for a party
 * Uses the vouchers endpoint with party_id and voucher_type_id filters
 * Must pass siteIds to search across all user's accessible sites
 */
export const getRecentOrdersForParty = async (
    partyId: number,
    siteIds: number[],
    limit: number = 5
): Promise<LastOrderInfo[]> => {
    try {
        // Build filters for party_id, voucher_type (Sales Invoice = 1), and sites
        const filters: Array<{ field: string; op: string; value: number | number[] }> = [
            { field: 'party_id', op: 'eq', value: partyId },
            { field: 'voucher_type_id', op: 'eq', value: 1 }, // Sales Invoice
        ];

        // Add site filter to search across all user's sites
        if (siteIds.length > 0) {
            filters.push({ field: 'site_id', op: 'in', value: siteIds });
        }

        const params = {
            limit,
            sort_by: 'transaction_date',
            sort_dir: 'desc',
            filters: JSON.stringify(filters),
        };

        const response = await axiosInstance.get('/vouchers/', { params });
        const vouchers = response.data.items || [];

        return vouchers.map((voucher: {
            id: number;
            voucher_number: string;
            transaction_date: string;
            total_amount: string;
            payment_status: string | null;
            amount_due: string | null;
        }) => ({
            id: voucher.id,
            voucher_number: voucher.voucher_number,
            transaction_date: voucher.transaction_date,
            total_amount: voucher.total_amount,
            payment_status: voucher.payment_status,
            amount_due: voucher.amount_due,
        }));
    } catch (error) {
        console.error('Failed to fetch recent orders for party:', error);
        return [];
    }
};
